export interface PricingTier {
  name: string
  price: string
  cadence: string
  desc: string
  features: string[]
  cta: string
  highlight?: boolean
}

interface Props {
  tier: PricingTier
  onSelect: () => void
  disabled?: boolean
  /** Override the CTA label (e.g. "Redirecting…" while a checkout fires). */
  ctaOverride?: string
}

/**
 * Shared pricing-tier card used by the landing page's Pricing section and the
 * standalone /pricing page. The card renders the visual; the caller decides
 * what `onSelect` does (navigate, fire checkout, mailto, etc.).
 */
export function PricingTierCard({ tier, onSelect, disabled, ctaOverride }: Props) {
  return (
    <div className="card card-pad" style={{
      borderColor: tier.highlight ? 'var(--accent-line)' : 'var(--line-soft)',
      background: tier.highlight ? 'color-mix(in oklch, var(--accent) 4%, var(--bg-1))' : 'var(--bg-1)',
      position: 'relative', display: 'flex', flexDirection: 'column', gap: 18,
    }}>
      {tier.highlight && (
        <span className="chip" style={{ position: 'absolute', top: -10, left: 18, fontSize: 10, background: 'var(--accent)', color: 'var(--bg)', border: 'none' }}>
          most popular
        </span>
      )}
      <div>
        <div className="mono" style={{ fontSize: 12, color: 'var(--fg-2)', marginBottom: 8, letterSpacing: '0.04em' }}>
          {tier.name.toLowerCase()}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{
            fontSize: 36, fontWeight: 700, letterSpacing: '-0.03em',
            color: tier.highlight ? 'var(--accent)' : 'var(--fg)',
          }}>{tier.price}</span>
          <span className="muted-2" style={{ fontSize: 13 }}>{tier.cadence}</span>
        </div>
        <p className="muted" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{tier.desc}</p>
      </div>
      <div style={{ height: 1, background: 'var(--line-soft)' }}></div>
      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
        {tier.features.map(f => (
          <li key={f} className="row gap-8" style={{ fontSize: 13, color: 'var(--fg-1)' }}>
            <span style={{
              color: tier.highlight ? 'var(--accent)' : 'var(--fg-3)',
              fontFamily: 'var(--mono)', fontSize: 11, marginTop: 1,
            }}>›</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        className={tier.highlight ? 'btn btn-primary' : 'btn btn-ghost'}
        onClick={onSelect}
        disabled={disabled}
        style={{ marginTop: 'auto', justifyContent: 'center', width: '100%' }}>
        {ctaOverride ?? tier.cta}
      </button>
    </div>
  )
}
